import { type MouseEvent } from 'react';
import { type IntlShape } from 'react-intl';

import { logToSentry, useFragmentContext } from '@jsmdg/react-fragment-scripts/fragment';
import {
    AlgoliaAnalyticsEventAction,
    AlgoliaAnalyticsEventPage,
    AlgoliaAnalyticsEventSource,
    GA4EventName,
    GA4FeatureCategory,
    GA4ItemListName,
    sendAlgoliaObjectIdsClickedEvent,
    sendAlgoliaObjectIdsViewedEvent,
    trackEecProductClick,
    trackEecProductImpression,
} from '@jsmdg/tracking';
import { type FragmentContext } from '../../shared/types/fragmentContext';
import { type Product } from '../../shared/types/searchResponse';
import { enhanceUrlWithSearchParams } from '../helper/enhanceUrlWithSearchParams';
import { useImpressionTracking } from './useImpressionTracking';
import { useInsightsClient } from './useInsightsClient';
import { useProductTileData } from './useProductTileData';

type UseOnProductTileClickAndImpressionReturnType = {
    setImpressionTrackedElement: (element: HTMLAnchorElement | HTMLDivElement | null) => void;
    onProductTileClick: (event?: MouseEvent<HTMLButtonElement>) => Promise<void>;
    sendObjectClick: () => Promise<void>;
};

const useOnProductTileClickAndImpression = (
    product: Product,
    position: number,
    intl: IntlShape,
    listInfo: {
        indexName?: string;
        queryId?: string;
        trackingListName?: GA4ItemListName;
        hasLocationFilter?: boolean;
    },
): UseOnProductTileClickAndImpressionReturnType => {
    const { indexName, queryId, trackingListName, hasLocationFilter } = listInfo;
    const { algoliaTracking } = useFragmentContext<FragmentContext>();
    const insightsClientRef = useInsightsClient();
    const resolvedIndexName = indexName || algoliaTracking?.indexName;

    const { url, productTrackingData } = useProductTileData(product, intl, {
        indexName,
        queryId,
        trackingListName,
        hasLocationFilter,
    });

    const productTrackingPayload = {
        product: productTrackingData,
        position,
        list: trackingListName,
        featureCategory: GA4FeatureCategory.ProductList,
        itemListName: trackingListName,
        isLocationSelected: hasLocationFilter,
    };

    const objectEventSource = ((): AlgoliaAnalyticsEventSource => {
        switch (trackingListName) {
            case GA4ItemListName.Bestseller:
                return AlgoliaAnalyticsEventSource.Bestseller;
            case GA4ItemListName.Highlight:
                return AlgoliaAnalyticsEventSource.Highlight;
            case GA4ItemListName.LastSeen:
                return AlgoliaAnalyticsEventSource.LastSeen;
            default:
                return AlgoliaAnalyticsEventSource.ProductList;
        }
    })();

    const sendObjectEvent = async (action: AlgoliaAnalyticsEventAction): Promise<void> => {
        if (!algoliaTracking?.userToken || !resolvedIndexName) {
            return;
        }

        try {
            const insightsClient = (await insightsClientRef.current)?.default;

            if (!insightsClient) {
                return;
            }

            const eventName = {
                action,
                page: AlgoliaAnalyticsEventPage.Home,
                source: objectEventSource,
            };

            if (action === AlgoliaAnalyticsEventAction.ProductViewed) {
                sendAlgoliaObjectIdsViewedEvent({
                    eventName,
                    indexName: resolvedIndexName,
                    insightsClient,
                    objectIds: [product.productId],
                });
                return;
            }

            sendAlgoliaObjectIdsClickedEvent({
                eventName,
                indexName: resolvedIndexName,
                insightsClient,
                objectIds: [product.productId],
            });
        } catch (error) {
            logToSentry(error instanceof Error ? error : new Error('Algolia Insights failed'));
        }
    };

    const setImpressionTrackedElement = useImpressionTracking(() => {
        trackEecProductImpression({
            ...productTrackingPayload,
            eventName: GA4EventName.ViewItemList,
        });
        void sendObjectEvent(AlgoliaAnalyticsEventAction.ProductViewed);
    });

    const onProductTileClick = async (event?: MouseEvent<HTMLButtonElement>): Promise<void> => {
        event?.preventDefault();
        await sendObjectEvent(AlgoliaAnalyticsEventAction.ProductClicked);
        window.location.href = enhanceUrlWithSearchParams(url);
        await window.yieldToMainThread();

        trackEecProductClick(productTrackingPayload);
    };

    const sendObjectClick = async (): Promise<void> =>
        sendObjectEvent(AlgoliaAnalyticsEventAction.ProductClicked);

    return { setImpressionTrackedElement, onProductTileClick, sendObjectClick };
};

export { useOnProductTileClickAndImpression };
